// hooks/billing/useUsageSummary.js
import { useCallback, useEffect, useState } from 'react';
import BillingService from '../../services/billing.service';

/**
 * useUsageSummary — `GET /accounts/{id}/billing/usage-summary`.
 *
 * Auto-refetches when `accountId`, `since` or `until` change. When
 * since/until are omitted the backend returns the current period.
 *
 * @param {string} accountId
 * @param {Object} [params]
 * @param {string} [params.since]   ISO 8601
 * @param {string} [params.until]   ISO 8601
 * @param {boolean} [params.auto=true]
 */
export function useUsageSummary(accountId, { since, until, auto = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(auto && accountId));
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    const result = await BillingService.getUsageSummary(accountId, {
      since,
      until,
    });
    if (result.success) {
      setData(result.data);
    } else {
      setError(result);
    }
    setLoading(false);
  }, [accountId, since, until]);

  useEffect(() => {
    if (auto && accountId) {
      refetch();
    } else if (!accountId) {
      setData(null);
      setError(null);
      setLoading(false);
    }
  }, [auto, accountId, refetch]);

  return { data, loading, error, refetch };
}

export default useUsageSummary;
